import { ComponentType } from "react";
import { NonEmptyArray } from "../../shared/types";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

export type Tab<T> = {
	id: string;
	label: string;
	Component: ComponentType<T>;
	isDefault?: boolean;
	expanded?: boolean;
	viewport?: boolean;
	onSelect?: () => void;
	partial?: boolean;
	status?: "warn" | "info";
};

export type TabBarProps<T> = {
	options: NonEmptyArray<Tab<T>>;
	activeTabId: string;
	onTabChange: (tabId: string) => void;
	className?: string;
	minimal?: boolean;
	/** Keep the small label even when the viewport is wide. */
	compact?: boolean;
	/** Each tab is tagged `{trackingPrefix}-{tabId}`. */
	trackingPrefix?: string;
};

export const getDefaultTab = <T,>(tabs: NonEmptyArray<Tab<T>>): Tab<T> =>
	tabs.find((tab) => tab.isDefault) ?? tabs[0];

export const TabBar = <T,>({
	options,
	activeTabId,
	onTabChange,
	className,
	minimal,
	compact,
	trackingPrefix,
}: TabBarProps<T>) => {
	return (
		<div className={cn("flex", className)}>
			{options.map((option) => (
				<div
					key={option.id}
					data-testid="tab-bar-option"
					{...trackingAttrs(trackingPrefix ? `${trackingPrefix}-${option.id}` : undefined)}
					className={cn(
						"flex justify-center shrink-0 whitespace-nowrap pt-2 px-4 font-medium text-xs cursor-pointer text-secondary border-bc-strong relative",
						!compact && "sm:text-sm",
						minimal ? "group pb-2" : "border-b-2 pb-[6px]",
						!minimal && !compact && "md:text-base",
						activeTabId === option.id
							? "text-link border-bc-focus cursor-default active"
							: minimal
								? "hover:text-secondary"
								: "hover:text-link-hover hover:border-link-hover",
					)}
					onClick={() => {
						onTabChange(option.id);
					}}
				>
					<div className="flex items-center gap-[6px]">
						<span>{option.label}</span>
						{(option.partial || option.status) && (
							<div
								data-testid="tab-indicator"
								className={cn(
									"size-2 mt-[2px] rounded-full",
									option.partial || option.status === "warn" ? "bg-warning" : "bg-intent-info-text",
								)}
							/>
						)}
					</div>
					{minimal && (
						<span
							className={cn(
								"absolute bottom-0",
								activeTabId === option.id
									? "h-[1px] left-[20px] right-[20px] bg-link"
									: "group-hover:h-0.5 group-hover:left-[20px] group-hover:right-[20px] group-hover:bg-surface-neutral",
							)}
						/>
					)}
				</div>
			))}
		</div>
	);
};
